import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";

const TableProducts = ({
  products,
  fields,
  register,
  errors,
  remove,
  append,
  compactView, // <-- new prop to know if we're in col-6 mode
  isFromPdf = false, // <-- new prop to indicate if products are from PDF
  trigger, // <-- new prop to trigger form validation
  triggerTotalsUpdate, // <-- new prop to trigger totals update
}) => {
  const { t } = useTranslation();
  
  // Watch for changes in products and trigger totals update
  useEffect(() => {
    if (products && products.length > 0) {
      if (triggerTotalsUpdate) {
        triggerTotalsUpdate();
      }
    }
  }, [products]); // Remove triggerTotalsUpdate from dependencies to prevent infinite loop
  
  // Handle input change to trigger form update
  const handleInputChange = (fieldName, value) => {
    // Trigger form validation to update totals
    if (trigger) {
      trigger('products');
    }
    // Trigger totals update
    if (triggerTotalsUpdate) {
      triggerTotalsUpdate();
    }
  };

  return (
    <div className="section">
      <h4>
        {t("depense.table.title")}
        {isFromPdf && (
          <span className="pdf-indicator">
            📄 {products.length} produit(s) extrait(s) du PDF
          </span>
        )}
      </h4>

      {!compactView ? (
        // ===== Normal Table for col-12 =====
        <table className="product-table">
          <thead>
            <tr>
              <th>{t("depense.table.name")}</th>
              <th>{t("depense.table.quantity")}</th>
              <th>{t("depense.table.price_ht")}</th>
              <th>{t("depense.table.tva")}</th>
              <th>{t("depense.table.description")}</th>
              <th>{t("depense.table.remise")}</th>
              <th>{t("depense.table.total")}</th>
              <th>{t("depense.table.action")}</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((item, index) => {
              const q = Number(products[index]?.quantity) || 0;
              const p = Number(products[index]?.priceHT) || 0;
              const tva = Number(products[index]?.tva) || 0;
              const remise = Number(products[index]?.remise) || 0;
              const ht = q * p - remise;
              const tvaValue = (ht * tva) / 100;
              const total = ht + tvaValue;

              return (
                <tr key={item.id}>
                  <td>
                    <input
                      {...register(`products.${index}.name`, {
                        required: true,
                      })}
                      className={errors.products?.[index]?.name ? "error" : ""}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      {...register(`products.${index}.quantity`, {
                        valueAsNumber: true,
                        required: true,
                        onChange: (e) => handleInputChange(`products.${index}.quantity`, e.target.value)
                      })}
                      className={
                        errors.products?.[index]?.quantity ? "error" : ""
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      {...register(`products.${index}.priceHT`, {
                        valueAsNumber: true,
                        required: true,
                        onChange: (e) => handleInputChange(`products.${index}.priceHT`, e.target.value)
                      })}
                      className={
                        errors.products?.[index]?.priceHT ? "error" : ""
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      {...register(`products.${index}.tva`, {
                        valueAsNumber: true,
                        required: true,
                        onChange: (e) => handleInputChange(`products.${index}.tva`, e.target.value)
                      })}
                      className={errors.products?.[index]?.tva ? "error" : ""}
                    />
                  </td>
                  <td>
                    <input {...register(`products.${index}.description`)} />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      {...register(`products.${index}.remise`, {
                        valueAsNumber: true,
                        onChange: (e) => handleInputChange(`products.${index}.remise`, e.target.value)
                      })}
                    />
                  </td>
                  <td>{total.toFixed(2)} €</td>
                  <td>
                    <button type="button" onClick={() => remove(index)}>
                      <DeleteIcon />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        // ===== Card/Grid layout for col-6 =====
        <div className="product-card-grid">
          {fields.map((item, index) => {
            const q = Number(products[index]?.quantity) || 0;
            const p = Number(products[index]?.priceHT) || 0;
            const tva = Number(products[index]?.tva) || 0;
            const remise = Number(products[index]?.remise) || 0;
            const ht = q * p - remise;
            const tvaValue = (ht * tva) / 100;
            const total = ht + tvaValue;

            return (
              <div key={item.id} className="product-card">
                <div>
                  <label>{t("depense.table.name")}</label>
                  <input
                    {...register(`products.${index}.name`, { required: true })}
                    className={errors.products?.[index]?.name ? "error" : ""}
                  />
                </div>
                <div>
                  <label>{t("depense.table.quantity")}</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register(`products.${index}.quantity`, {
                      valueAsNumber: true,
                      required: true,
                    })}
                  />
                </div>
                <div>
                  <label>{t("depense.table.price_ht")}</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register(`products.${index}.priceHT`, {
                      valueAsNumber: true,
                      required: true,
                    })}
                  />
                </div>
                <div>
                  <label>{t("depense.table.tva")}</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register(`products.${index}.tva`, {
                      valueAsNumber: true,
                      required: true,
                    })}
                  />
                </div>
                <div>
                  <label>{t("depense.table.description")}</label>
                  <input {...register(`products.${index}.description`)} />
                </div>
                <div>
                  <label>{t("depense.table.remise")}</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register(`products.${index}.remise`, {
                      valueAsNumber: true,
                    })}
                  />
                </div>
                <div className="total-line">
                  <strong>
                    {t("depense.table.total")}: {total.toFixed(2)} €
                  </strong>
                </div>
                <button
                  type="button"
                  className="btn-delete"
                  onClick={() => remove(index)}
                >
                  <DeleteIcon />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        className="btn-add"
        onClick={() =>
          append({
            name: "",
            quantity: 1,
            priceHT: 0,
            tva: 20,
            description: "",
            remise: 0,
          })
        }
      >
        ➕ {t("depense.table.add_product")}
      </button>
    </div>
  );
};

export default TableProducts;
